/**
 * Utility functions for handling dates and Firebase timestamps
 */

/**
 * Convert Firebase timestamp to JavaScript Date object
 * Handles multiple Firebase timestamp formats:
 * - { _seconds, _nanoseconds }
 * - { seconds, nanoseconds }
 * - Date objects
 * - ISO date strings
 * 
 * @param {Object|Date|string} timestamp - Firebase timestamp or date
 * @returns {Date|null} JavaScript Date object or null if invalid
 */
export const convertFirebaseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // If it's already a Date object, return it
  if (timestamp instanceof Date) return timestamp;
  
  // If it's a string, parse it
  if (typeof timestamp === 'string') return new Date(timestamp);
  
  // If it's a Firebase timestamp object with _seconds and _nanoseconds
  if (timestamp._seconds !== undefined) {
    const milliseconds = timestamp._seconds * 1000 + Math.floor((timestamp._nanoseconds || 0) / 1000000);
    return new Date(milliseconds);
  }
  
  // If it's a timestamp object with seconds property (Firestore format)
  if (timestamp.seconds !== undefined) {
    const milliseconds = timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1000000);
    return new Date(milliseconds);
  }
  
  // If it's a Firestore Timestamp object with toDate method
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  return null;
};

/**
 * Format a date for display in various formats
 * 
 * @param {Object|Date|string} date - Date to format
 * @param {string} format - Format type: 'short', 'long', 'datetime', 'time'
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'long') => {
  const dateObj = convertFirebaseTimestamp(date);
  
  if (!dateObj || isNaN(dateObj.getTime())) return 'N/A';
  
  const options = {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    },
    datetime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    },
    time: {
      hour: '2-digit',
      minute: '2-digit'
    },
    full: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }
  };
  
  return dateObj.toLocaleDateString('en-US', options[format] || options.long);
};

/**
 * Format a relative time (e.g., "2 hours ago", "3 days ago")
 * 
 * @param {Object|Date|string} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  const dateObj = convertFirebaseTimestamp(date);
  
  if (!dateObj || isNaN(dateObj.getTime())) return 'N/A';
  
  const now = new Date();
  const diffMs = now - dateObj;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
  return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
};

/**
 * Check if a date is valid
 * 
 * @param {Object|Date|string} date - Date to check
 * @returns {boolean} True if valid date
 */
export const isValidDate = (date) => {
  const dateObj = convertFirebaseTimestamp(date);
  return dateObj !== null && !isNaN(dateObj.getTime());
};

/**
 * Format time duration in minutes to human-readable format
 * 
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0 min';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

